import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  BarChart3,
  CalendarCheck,
  ChevronDown,
  ClipboardCheck,
  Layers,
  MessageSquare,
  Minus,
  PencilRuler,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Video,
} from 'lucide-react';
import { Badge, Button, cn } from '@tutor/ui';
import { api } from '../../lib/api-client';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { ClassroomPreview } from '../../components/marketing/ClassroomPreview';

interface PublicSubject {
  name: string;
  slug: string;
  colour: string | null;
  levels: string[];
  courses: Array<{ name: string; examBoard: string | null; topicCount: number }>;
}

const STEPS = [
  {
    icon: UserPlus,
    title: 'Create your account',
    body: 'Sign up as a student or a tutor in under a minute. Tutors invite their students by email; students accept and the two are connected.',
  },
  {
    icon: CalendarCheck,
    title: 'Book the lesson',
    body: 'Your tutor schedules the session against a shared calendar that refuses double bookings. Everyone sees the same timetable.',
  },
  {
    icon: Video,
    title: 'Learn in the classroom',
    body: 'Join from the browser fifteen minutes before you start. Shared whiteboard, video, chat — nothing to install.',
  },
];

const CLASSROOM_FEATURES = [
  {
    icon: PencilRuler,
    title: 'A whiteboard you both write on',
    body: 'Pens, highlighter, shapes and an eraser, with every stroke appearing on the other screen as it is drawn. Live cursors show exactly where your tutor is pointing.',
  },
  {
    icon: Video,
    title: 'Face to face, peer to peer',
    body: 'Camera, microphone and screen sharing run directly between the two of you. Turn them on and off mid-lesson without dropping the call.',
  },
  {
    icon: Layers,
    title: 'Multiple pages, saved for later',
    body: 'Add a fresh page when one fills up. Everything is stored, so a student who reconnects sees the full board exactly as it was.',
  },
  {
    icon: MessageSquare,
    title: 'Chat alongside the board',
    body: 'Share a link or ask a quick question without interrupting. The transcript stays attached to the lesson.',
  },
];

const PARENT_FEATURES = [
  {
    icon: ClipboardCheck,
    title: 'Homework that actually closes the loop',
    body: 'Tutors set work with instructions and a mark scheme. Students submit in the portal. Marks, strengths and next steps come back attached to the same piece of work.',
  },
  {
    icon: BarChart3,
    title: 'Progress you can see',
    body: 'Attainment per subject, mastery per topic, and homework completion — so "how are they getting on?" has a real answer.',
  },
  {
    icon: ShieldCheck,
    title: 'Private by default',
    body: 'Every lesson is locked to its participants and checked on the server. A student can only ever open their own classroom.',
  },
];

const FAQS = [
  {
    question: 'Do I need to install anything?',
    answer:
      'No. The classroom runs in a modern browser — Chrome, Edge, Safari or Firefox. You will be asked for camera and microphone permission the first time you turn them on, and you can join with both switched off.',
  },
  {
    question: 'How do a tutor and student get connected?',
    answer:
      'A tutor invites a student by email address. The student sees the invitation on their dashboard the next time they sign in and accepts it. Nothing is shared until they do — a tutor cannot add someone to their roster on their own.',
  },
  {
    question: 'What if the student does not have an account yet?',
    answer:
      'The invitation waits. When they register with that email address it appears straight away, so you can invite somebody before they have signed up.',
  },
  {
    question: 'Can a student see another student’s lesson?',
    answer:
      'No. Access is decided on the server from the lesson’s participant list, not from the page you happen to be on. Changing the address bar returns a permission error, and the same check guards the live classroom connection.',
  },
  {
    question: 'What happens to work after the lesson?',
    answer:
      'Whiteboard pages, chat and lesson notes stay attached to the lesson. Homework keeps its submission, mark and feedback together, so both sides can look back at it.',
  },
  {
    question: 'Which subjects and levels are covered?',
    answer:
      'The curriculum is configurable. Out of the box it carries A-Level and GCSE courses in Mathematics, Chemistry and Physics, broken into topics that lessons and homework can be tagged against.',
  },
];

export function LandingPage() {
  const { data: subjects } = useQuery({
    queryKey: ['public', 'subjects'] as const,
    queryFn: () => api.get<PublicSubject[]>('/api/public/subjects'),
    staleTime: 60 * 60_000,
  });

  return (
    <PublicLayout>
      {/* ------------------------------- hero ------------------------------- */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_50%_0%,theme(colors.brand.50),transparent)]"
          aria-hidden
        />

        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700">
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                One-to-one online tutoring
              </span>

              <h1 className="mt-5 text-4xl font-semibold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
                Tutoring that carries on
                <span className="text-brand-600"> between lessons</span>
              </h1>

              <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">
                A live classroom with a shared whiteboard, homework that gets set and
                marked in the same place, and progress both the student and their tutor can
                actually see. No spreadsheets, no lost worksheets, no guessing.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register?role=STUDENT">
                  <Button size="lg" rightIcon={<ArrowRight className="h-4 w-4" aria-hidden />}>
                    Start learning
                  </Button>
                </Link>
                <Link to="/register?role=TUTOR">
                  <Button size="lg" variant="outline">
                    Tutor with us
                  </Button>
                </Link>
              </div>

              <dl className="mt-10 grid max-w-lg grid-cols-3 gap-4 border-t border-slate-200 pt-6">
                {[
                  { label: 'Setup', value: 'No install' },
                  { label: 'Classroom', value: 'Browser only' },
                  { label: 'Lesson history', value: 'Kept' },
                ].map((item) => (
                  <div key={item.label}>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{item.label}</dt>
                    <dd className="mt-0.5 text-sm font-semibold text-slate-900">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="lg:pl-4">
              <ClassroomPreview />
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- how it works --------------------------- */}
      <section id="how-it-works" className="scroll-mt-20 border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <SectionHeading
            eyebrow="How it works"
            title="Three steps to the first lesson"
            body="The same flow whether you are a tutor bringing your own students or a student joining one."
          />

          <ol className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((step, index) => (
              <li key={step.title} className="relative rounded-xl border border-slate-200 bg-white p-6">
                <span className="absolute -top-3 left-6 flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
                  {index + 1}
                </span>
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                  <step.icon className="h-5 w-5" aria-hidden />
                </span>
                <h3 className="mt-4 font-semibold text-slate-900">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ----------------------------- classroom ---------------------------- */}
      <section id="classroom" className="scroll-mt-20">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <SectionHeading
            eyebrow="The classroom"
            title="Built for teaching, not just talking"
            body="A video call is not a lesson. The classroom gives you the things a tutor actually needs in front of them."
          />

          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {CLASSROOM_FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="rounded-xl border border-slate-200 bg-white p-6 transition-colors hover:border-brand-200"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                  <feature.icon className="h-5 w-5" aria-hidden />
                </span>
                <h3 className="mt-4 font-semibold text-slate-900">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{feature.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------ subjects ---------------------------- */}
      <section id="subjects" className="scroll-mt-20 border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <SectionHeading
            eyebrow="Subjects and levels"
            title="Courses broken down to the topic"
            body="Lessons, homework and progress all hang off the same topic list, so you can see exactly which part of a course needs work."
          />

          {subjects && subjects.length > 0 ? (
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {subjects.map((subject) => (
                <div key={subject.slug} className="rounded-xl border border-slate-200 bg-white p-6">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: subject.colour ?? '#6366f1' }}
                      aria-hidden
                    />
                    <h3 className="font-semibold text-slate-900">{subject.name}</h3>
                  </div>

                  <ul className="mt-4 space-y-3">
                    {subject.courses.map((course) => (
                      <li key={course.name}>
                        <p className="text-sm font-medium text-slate-900">{course.name}</p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {course.examBoard ? `${course.examBoard} · ` : ''}
                          {course.topicCount} topics
                        </p>
                      </li>
                    ))}
                  </ul>

                  {subject.levels.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5 border-t border-slate-100 pt-4">
                      {subject.levels.map((level) => (
                        <Badge key={level} tone="brand">
                          {level.replace('_', '-')}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-12 text-center text-sm text-slate-500">
              Subjects are configured per platform — Mathematics, Chemistry and Physics ship as standard.
            </p>
          )}
        </div>
      </section>

      {/* ------------------------------ parents ----------------------------- */}
      <section id="parents" className="scroll-mt-20">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <SectionHeading
                align="left"
                eyebrow="For students and parents"
                title="Know where you stand, week to week"
                body="Everything a lesson produces lands in one place, so nobody has to reconstruct what happened."
              />
            </div>

            <ul className="space-y-6">
              {PARENT_FEATURES.map((feature) => (
                <li key={feature.title} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                    <feature.icon className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <h3 className="font-semibold text-slate-900">{feature.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{feature.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ----------------------------- for tutors --------------------------- */}
      <section id="for-tutors" className="scroll-mt-20 bg-brand-700 text-white">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white">
                For tutors
              </span>
              <h2 className="mt-5 text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
                Bring your students. Keep the admin small.
              </h2>
              <p className="mt-4 max-w-lg text-brand-100">
                Invite the students you already teach, schedule against one calendar, and set
                homework that comes back to you marked-ready. The teaching tools are the
                point — the paperwork just stops being your problem.
              </p>

              <div className="mt-8">
                <Link to="/register?role=TUTOR">
                  <Button
                    size="lg"
                    className="bg-white text-brand-700 hover:bg-brand-50"
                    rightIcon={<ArrowRight className="h-4 w-4" aria-hidden />}
                  >
                    Create a tutor account
                  </Button>
                </Link>
              </div>
            </div>

            <ul className="grid gap-4 sm:grid-cols-2">
              {[
                { title: 'Your own roster', body: 'Invite by email. Students confirm before anything is shared.' },
                { title: 'Clash-free calendar', body: 'Double bookings are refused before they happen.' },
                { title: 'Marking in one screen', body: 'Score, grade, strengths and next steps together.' },
                { title: 'Reusable homework', body: 'Write it once, set it to any student, any term.' },
              ].map((item) => (
                <li key={item.title} className="rounded-xl bg-white/10 p-5 backdrop-blur-sm">
                  <h3 className="text-sm font-semibold">{item.title}</h3>
                  <p className="mt-1.5 text-sm text-brand-100">{item.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* -------------------------------- FAQ ------------------------------- */}
      <section id="faq" className="scroll-mt-20 border-b border-slate-200">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:py-20">
          <SectionHeading eyebrow="Questions" title="The things people ask first" />

          <div className="mt-10 divide-y divide-slate-200 border-y border-slate-200">
            {FAQS.map((faq) => (
              <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------- final CTA ---------------------------- */}
      <section>
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-6 py-12 text-center sm:px-12">
            <h2 className="text-3xl font-semibold tracking-tight text-slate-900">
              Ready for the first lesson?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-slate-600">
              Create an account and you are two clicks from a classroom. Students join in
              seconds; tutors can invite their first student straight away.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/register">
                <Button size="lg" rightIcon={<ArrowRight className="h-4 w-4" aria-hidden />}>
                  Create your account
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline">
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function SectionHeading({
  eyebrow,
  title,
  body,
  align = 'center',
}: {
  eyebrow: string;
  title: string;
  body?: string;
  align?: 'center' | 'left';
}) {
  return (
    <div className={cn(align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-xl')}>
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{eyebrow}</p>
      <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{title}</h2>
      {body && <p className="mt-4 text-slate-600">{body}</p>}
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-4 text-left"
      >
        <span className="font-medium text-slate-900">{question}</span>
        <span className="shrink-0 text-slate-400">
          {open ? <Minus className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
        </span>
      </button>
      {open && <p className="pb-4 pr-8 text-sm leading-relaxed text-slate-600">{answer}</p>}
    </div>
  );
}
